/* eslint-disable import/no-default-export */
import { trelloFetch } from '@trello/fetch';
import { clientVersion } from '@trello/config';
import { token } from '@trello/session-cookie';
// eslint-disable-next-line no-restricted-imports
import { normalizeMember } from 'app/gamma/src/api/normalizers/member';
// eslint-disable-next-line no-restricted-imports
import type { LoadBoardSuccessAction } from 'app/gamma/src/modules/loaders/load-board';
// eslint-disable-next-line no-restricted-imports
import { LOAD_BOARD_SUCCESS } from 'app/gamma/src/modules/loaders/load-board';
// eslint-disable-next-line no-restricted-imports
import type { LoadHeaderSuccessAction } from 'app/gamma/src/modules/loaders/load-header';
// eslint-disable-next-line no-restricted-imports
import { LOAD_HEADER_SUCCESS } from 'app/gamma/src/modules/loaders/load-header';
import type { LoadMyBoardsSuccessAction } from 'app/src/components/ReduxSearch/loadBoardsForSearch';
import { LOAD_MY_BOARDS_SUCCESS } from 'app/src/components/ReduxSearch/loadBoardsForSearch';
// eslint-disable-next-line no-restricted-imports
import type { LoadMemberSuccessAction } from 'app/gamma/src/modules/loaders/load-member';
// eslint-disable-next-line no-restricted-imports
import { LOAD_MEMBER_SUCCESS } from 'app/gamma/src/modules/loaders/load-member';
// eslint-disable-next-line no-restricted-imports
import type {
  SocketMemberAction,
  SocketDropNonPublic,
} from 'app/gamma/src/modules/sockets';
// eslint-disable-next-line no-restricted-imports
import {
  isDeletedModel,
  SOCKET_MEMBER,
  SOCKET_DROP_NON_PUBLIC,
} from 'app/gamma/src/modules/sockets';
// eslint-disable-next-line no-restricted-imports
import type { MemberResponse } from 'app/gamma/src/types/responses';
// eslint-disable-next-line no-restricted-imports
import { getMe } from 'app/gamma/src/selectors/members';
// eslint-disable-next-line no-restricted-imports
import type {
  MemberMessagesDismissedModel,
  MemberModel,
  NotificationEmailFrequency,
} from 'app/gamma/src/types/models';
// eslint-disable-next-line no-restricted-imports
import type { StandardThunkAction, Dispatch } from 'app/gamma/src/types';
// eslint-disable-next-line no-restricted-imports
import type { Action } from '@trello/redux';
// eslint-disable-next-line no-restricted-imports
import { actionCreator, createReducer } from '@trello/redux';
// eslint-disable-next-line no-restricted-imports
import type { UpdateMemberPrefsAction } from 'app/gamma/src/modules/state/ui/member-home-profile';
// eslint-disable-next-line no-restricted-imports
import { UPDATE_MEMBER_PREFS } from 'app/gamma/src/modules/state/ui/member-home-profile';
import type {
  GetSearchSuggestionsAction,
  PerformSearchSuccessAction,
} from 'app/src/components/ReduxSearch/search-actions';
import {
  PERFORM_SEARCH_SUCCESS,
  SET_SEARCHED_SUGGESTIONS,
} from 'app/src/components/ReduxSearch/search-actions';
// eslint-disable-next-line no-restricted-imports
import type { State } from 'app/gamma/src/modules/types';
import { Analytics } from '@trello/atlassian-analytics';

const UPDATE_MARKETING_OPT_IN_REQUEST = Symbol(
  'models/UPDATE_MARKETING_OPT_IN_REQUEST',
);
const UPDATE_MARKETING_OPT_IN_SUCCESS = Symbol(
  'models/UPDATE_MARKETING_OPT_IN_SUCCESS',
);
const UPDATE_MARKETING_OPT_IN_ERROR = Symbol(
  'models/UPDATE_MARKETING_OPT_IN_ERROR',
);
const DISMISS_MESSAGE_REQUEST = Symbol('models/DISMISS_MESSAGE_REQUEST');
const DISMISS_MESSAGE_SUCCESS = Symbol('models/DISMISS_MESSAGE_SUCCESS');
const DISMISS_MESSAGE_ERROR = Symbol('models/DISMISS_MESSAGE_ERROR');

type UpdateMarketingOptInRequestAction = Action<
  typeof UPDATE_MARKETING_OPT_IN_REQUEST,
  { member: MemberModel; optedIn: boolean }
>;
type UpdateMarketingOptInSuccessAction = Action<
  typeof UPDATE_MARKETING_OPT_IN_SUCCESS,
  MemberResponse
>;
type UpdateMarketingOptInErrorAction = Action<
  typeof UPDATE_MARKETING_OPT_IN_ERROR,
  { member: MemberModel; error: Error }
>;
type DismissMessageRequestAction = Action<
  typeof DISMISS_MESSAGE_REQUEST,
  { member: MemberModel; dismissedMessage: MemberMessagesDismissedModel }
>;
type DismissMessageSuccessAction = Action<
  typeof DISMISS_MESSAGE_SUCCESS,
  MemberResponse
>;
type DismissMessageErrorAction = Action<
  typeof DISMISS_MESSAGE_ERROR,
  { member: MemberModel; name: string }
>;

const updateMarketingOptInRequest = actionCreator<UpdateMarketingOptInRequestAction>(
  UPDATE_MARKETING_OPT_IN_REQUEST,
);
const updateMarketingOptInSuccess = actionCreator<UpdateMarketingOptInSuccessAction>(
  UPDATE_MARKETING_OPT_IN_SUCCESS,
);
const updateMarketingOptInError = actionCreator<UpdateMarketingOptInErrorAction>(
  UPDATE_MARKETING_OPT_IN_ERROR,
);
const dismissMessageRequest = actionCreator<DismissMessageRequestAction>(
  DISMISS_MESSAGE_REQUEST,
);
const dismissMessageSuccess = actionCreator<DismissMessageSuccessAction>(
  DISMISS_MESSAGE_SUCCESS,
);
const dismissMessageError = actionCreator<DismissMessageErrorAction>(
  DISMISS_MESSAGE_ERROR,
);

// Reducer
export interface MemberState {
  [memberId: string]: MemberModel;
}

const initialState: MemberState = {};
const upsertMember = (state: MemberState, member: MemberResponse) => {
  return {
    ...state,
    [member.id]: normalizeMember(member, state[member.id]),
  };
};

export default createReducer(initialState, {
  [LOAD_BOARD_SUCCESS](
    state: MemberState,
    { payload }: LoadBoardSuccessAction,
  ) {
    const { members = [] } = payload;

    return members.reduce(
      (result, member) => upsertMember(result, member),
      state,
    );
  },

  [LOAD_MY_BOARDS_SUCCESS](
    state: MemberState,
    { payload }: LoadMyBoardsSuccessAction,
  ) {
    return upsertMember(state, payload);
  },

  [LOAD_HEADER_SUCCESS](
    state: MemberState,
    { payload }: LoadHeaderSuccessAction,
  ) {
    return upsertMember(state, payload);
  },

  [PERFORM_SEARCH_SUCCESS](
    state: MemberState,
    { payload }: PerformSearchSuccessAction,
  ) {
    const { members = [], cards = [] } = payload;
    const cardMembers = cards.reduce((result, card) => {
      return result.concat(card.members ? card.members : []);
    }, [] as MemberResponse[]);

    return members
      .concat(cardMembers)
      .reduce((result, m) => upsertMember(result, m), state);
  },

  [SET_SEARCHED_SUGGESTIONS](
    state: MemberState,
    { payload }: GetSearchSuggestionsAction,
  ) {
    const { members = [], cards = [] } = payload;
    const cardMembers = cards.reduce((result, card) => {
      return result.concat(card.members ? card.members : []);
    }, [] as MemberResponse[]);

    return members
      .concat(cardMembers)
      .reduce((result, m) => upsertMember(result, m), state);
  },

  [SOCKET_DROP_NON_PUBLIC](
    state: MemberState,
    { payload: memberId }: SocketDropNonPublic,
  ) {
    return {
      ...state,
      [memberId]: {
        ...state[memberId],
        nonPublic: {},
      },
    };
  },

  [UPDATE_MEMBER_PREFS](
    state: MemberState,
    { payload }: UpdateMemberPrefsAction,
  ) {
    return upsertMember(state, payload);
  },

  [SOCKET_MEMBER](state: MemberState, { payload }: SocketMemberAction) {
    if (isDeletedModel(payload)) {
      const { [payload.id]: removed, ...remaining } = state;

      return remaining;
    }
    return upsertMember(state, payload);
  },

  [LOAD_MEMBER_SUCCESS](
    state: MemberState,
    { payload }: LoadMemberSuccessAction,
  ) {
    return upsertMember(state, payload);
  },

  [UPDATE_MARKETING_OPT_IN_REQUEST](
    state: MemberState,
    { payload }: UpdateMarketingOptInRequestAction,
  ) {
    const { member, optedIn } = payload;

    return {
      ...state,
      [member.id]: {
        ...member,
        marketingOptIn: {
          date: new Date().toISOString(),
          optedIn,
        },
      },
    };
  },

  [UPDATE_MARKETING_OPT_IN_SUCCESS](
    state: MemberState,
    { payload: member }: UpdateMarketingOptInSuccessAction,
  ) {
    return upsertMember(state, member);
  },

  [UPDATE_MARKETING_OPT_IN_ERROR](
    state: MemberState,
    { payload }: UpdateMarketingOptInErrorAction,
  ) {
    const { member } = payload;

    return {
      ...state,
      [member.id]: {
        ...member,
        marketingOptIn: {},
      },
    };
  },

  [DISMISS_MESSAGE_REQUEST](
    state: MemberState,
    { payload }: DismissMessageRequestAction,
  ) {
    const { member, dismissedMessage } = payload;
    const messagesDismissed = member.messagesDismissed || [];

    return {
      ...state,
      [member.id]: {
        ...member,
        messagesDismissed: [...messagesDismissed, dismissedMessage],
      },
    };
  },

  [DISMISS_MESSAGE_SUCCESS](
    state: MemberState,
    { payload: member }: DismissMessageSuccessAction,
  ) {
    return upsertMember(state, member);
  },

  [DISMISS_MESSAGE_ERROR](
    state: MemberState,
    { payload }: DismissMessageErrorAction,
  ) {
    const { member, name } = payload;
    const messagesDismissed = (member.messagesDismissed || []).filter(
      (message) => message.name !== name,
    );

    return {
      ...state,
      [member.id]: {
        ...member,
        messagesDismissed: [...messagesDismissed],
      },
    };
  },
});

const updateNotificationPreference = ({
  id,
  pref,
}: {
  id: string;
  pref: NotificationEmailFrequency;
}): StandardThunkAction => {
  return async (dispatch: Dispatch) => {
    try {
      const response = await trelloFetch(
        `/1/member/${id}?prefs%2FminutesBetweenSummaries=${pref}`,
        {
          headers: { 'Content-Type': 'application/json' },
          method: 'PUT',
          body: JSON.stringify({
            token,
          }),
        },
      );
      const member = await response.json();

      dispatch(actionCreator(UPDATE_MEMBER_PREFS)(member));

      Analytics.sendTrackEvent({
        action: 'updated',
        actionSubject: 'minutesBetweenSummaries',
        source: 'appHeader', // these actions are usually dispatched via the header, not the member prefs page
        attributes: {
          updatedOn: 'member',
          value: pref,
        },
      });
    } catch (err) {
      console.error(err);
    }
  };
};

export const updateNotificationEmailFrequency = (
  frequency: NotificationEmailFrequency,
): StandardThunkAction => {
  return async (dispatch: Dispatch, getState: () => State) => {
    const me = getMe(getState());

    if (!me) {
      return;
    }

    dispatch(
      updateNotificationPreference({
        id: me.id,
        pref: frequency,
      }),
    );
  };
};

export const updateMarketingOptIn = (
  optedIn: boolean,
  prompt: string,
): StandardThunkAction => {
  return async (dispatch: Dispatch, getState: () => State) => {
    const me = getMe(getState());
    if (!me) {
      return;
    }

    try {
      dispatch(
        updateMarketingOptInRequest({
          member: me,
          optedIn,
        }),
      );

      const memberResponse = await trelloFetch(
        '/1/members/me',
        {
          headers: { 'Content-Type': 'application/json' },
          method: 'PUT',
          body: JSON.stringify({
            'marketingOptIn/optedIn': optedIn,
            'marketingOptIn/displayText': prompt,
            token,
          }),
        },
        {
          clientVersion,
          networkRequestEventAttributes: {
            source: 'trelloFetch',
            operationName: 'updateMarketingOptIn',
          },
        },
      );

      const member: MemberResponse = await memberResponse.json();

      dispatch(updateMarketingOptInSuccess(member));

      // this is also being tracked in `marketing-opt-in-banner`, do we still need this?
      Analytics.sendTrackEvent({
        action: 'updated',
        actionSubject: 'optedIn',
        source: 'appHeader',
        attributes: {
          updatedOn: 'member',
          value: optedIn,
        },
      });
    } catch (err) {
      dispatch(
        updateMarketingOptInError({
          member: me,
          error: err,
        }),
      );
    }
  };
};

export const addDismissedMessage = (name: string): StandardThunkAction => {
  return async (dispatch: Dispatch, getState: () => State) => {
    const me = getMe(getState());
    if (!me) {
      return;
    }

    try {
      dispatch(
        dismissMessageRequest({
          member: me,
          dismissedMessage: {
            _id: '',
            count: 1,
            lastDismissed: new Date().toISOString(),
            name,
          },
        }),
      );

      const memberResponse = await trelloFetch(
        '/1/members/me/messagesDismissed',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, token }),
        },
        {
          clientVersion,
          networkRequestEventAttributes: {
            source: 'trelloFetch',
            operationName: 'addDismissedMessage',
          },
        },
      );

      const member = await memberResponse.json();

      dispatch(dismissMessageSuccess(member));
    } catch (err) {
      dispatch(
        dismissMessageError({
          member: me,
          name,
        }),
      );
    }
  };
};
